// utils/globalHoverFix.js
class GlobalHoverFix {
    constructor() {
        this.timeoutId = null
        this.isTouch = false
        this.touchStartTime = 0
        this.hoveredElements = new Set()

        this.init()
    }

    init() {
        // Определяем touch устройство
        this.detectTouchDevice()

        // Добавляем глобальные стили для плавных переходов
        this.injectGlobalStyles()

        // Устанавливаем слушатели событий
        this.setupEventListeners()

        // Отслеживаем новые элементы с hover
        this.observeHoverElements()
    }

    detectTouchDevice() {
        this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    }

    injectGlobalStyles() {
        const style = document.createElement('style')
        style.textContent = `
      /* Глобальные стили для плавного удаления hover */
      .hover-removing {
        transition: all 0.3s ease-out !important;
        pointer-events: none !important;
      }
      
      /* Отключение hover на touch устройствах */
      @media (hover: none) and (pointer: coarse) {
        *:hover {
          transition: all 0.3s ease-out !important;
        }
      }
      
      /* Предотвращение sticky hover */
      @media (pointer: coarse) {
        *:hover {
          transition: all 0.2s ease-out !important;
        }
      }
    `
        document.head.appendChild(style)
    }

    setupEventListeners() {
        // Touch события
        document.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true })
        document.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true })
        document.addEventListener('touchcancel', this.handleTouchEnd.bind(this), { passive: true })

        // Mouse события для очистки на desktop
        document.addEventListener('mouseleave', this.handleMouseLeave.bind(this), true)

        // Слушаем изменения в DOM
        document.addEventListener('DOMContentLoaded', this.handleDOMReady.bind(this))

        // Слушаем скролл для очистки hover
        document.addEventListener('scroll', this.handleScroll.bind(this), { passive: true })
    }

    handleTouchStart(event) {
        this.isTouch = true
        this.touchStartTime = Date.now()

        // Очищаем предыдущий таймер
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
    }

    handleTouchEnd(event) {
        if (!this.isTouch) return

        // Очищаем предыдущий таймер
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }

        // Устанавливаем таймер на удаление hover
        this.timeoutId = setTimeout(() => {
            this.removeAllHoverEffects()
        }, 500)
    }

    handleMouseLeave(event) {
        // Только для touch устройств
        if (this.isTouch && event.target) {
            this.removeHoverFromElement(event.target)
        }
    }

    handleScroll() {
        if (this.isTouch) {
            // Немедленно очищаем hover при скролле
            this.removeAllHoverEffects()
        }
    }

    handleDOMReady() {
        // Дополнительная инициализация после загрузки DOM
        setTimeout(() => {
            this.observeHoverElements()
        }, 100)
    }

    removeAllHoverEffects() {
        // Находим все элементы с активным hover
        const hoveredElements = document.querySelectorAll(':hover')

        hoveredElements.forEach(el => {
            this.removeHoverFromElement(el)
        })

        // Также очищаем отслеживаемые элементы
        this.hoveredElements.forEach(el => {
            if (document.contains(el)) {
                this.removeHoverFromElement(el)
            }
        })

        // Очищаем Set
        this.hoveredElements.clear()

        // Создаем фиктивный элемент для сброса focus
        this.createDummyFocusElement()
    }

    removeHoverFromElement(element) {
        if (!element || !element.style) return

        // Добавляем класс для плавного перехода
        element.classList.add('hover-removing')

        // Убираем focus
        if (element.blur) {
            element.blur()
        }

        // Создаем события для полного сброса hover
        const events = ['mouseleave', 'mouseout', 'blur']
        events.forEach(eventType => {
            const event = new Event(eventType, { bubbles: true, cancelable: true })
            element.dispatchEvent(event)
        })

        // Убираем класс через небольшую задержку
        setTimeout(() => {
            element.classList.remove('hover-removing')
        }, 300)
    }

    createDummyFocusElement() {
        const dummy = document.createElement('div')
        dummy.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    `
        dummy.setAttribute('tabindex', '-1')

        document.body.appendChild(dummy)
        dummy.focus()

        setTimeout(() => {
            if (document.body.contains(dummy)) {
                document.body.removeChild(dummy)
            }
        }, 100)
    }

    observeHoverElements() {
        // Отслеживаем элементы с hover через MutationObserver
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            this.trackHoverElements(node)
                        }
                    })
                }
            })
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        // Изначально отслеживаем все существующие элементы
        this.trackHoverElements(document.body)
    }

    trackHoverElements(container) {
        if (!container || !container.querySelectorAll) return

        // Находим все элементы с hover стилями
        const allElements = container.querySelectorAll('*')
        allElements.forEach(el => {
            const computedStyle = window.getComputedStyle(el, ':hover')
            if (computedStyle && computedStyle.length > 0) {
                this.hoveredElements.add(el)

                // Добавляем прямые слушатели на элементы с hover
                el.addEventListener('touchstart', () => {
                    this.hoveredElements.add(el)
                }, { passive: true })
            }
        })
    }

    // Публичные методы
    forceRemoveHover() {
        this.removeAllHoverEffects()
    }

    destroy() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }

        // Удаляем все слушатели
        document.removeEventListener('touchstart', this.handleTouchStart.bind(this))
        document.removeEventListener('touchend', this.handleTouchEnd.bind(this))
        document.removeEventListener('touchcancel', this.handleTouchEnd.bind(this))
        document.removeEventListener('mouseleave', this.handleMouseLeave.bind(this))
        document.removeEventListener('scroll', this.handleScroll.bind(this))
    }
}

// Создаем единственный экземпляр
const globalHoverFix = new GlobalHoverFix()

// Экспортируем для использования в Vue
export default globalHoverFix

// Также экспортируем plugin для Vue
export const HoverFixPlugin = {
    install(app) {
        app.config.globalProperties.$hoverFix = globalHoverFix
        app.provide('hoverFix', globalHoverFix)
    }
}